import { Grid, ListItemText, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FC, ReactNode, useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MoreTimeIcon from "@mui/icons-material/MoreTime";
import AddTaskIcon from "@mui/icons-material/AddTask";
import AppointmentTreeItemActionButtons from "@/components/appointmentDashboard/appointmentTreeItemActionButtons";
import DropDown from "@/components/forms/inputs/dropDown";
import styles from "@/components/appointmentDashboard/appointmentTreeItemHeaderbar.module.css";
import { getAppointmentDetails } from "@/helper/statusOptions";
import { Edit } from "@mui/icons-material";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import TodoCreateEditModal from "@/components/todos/todoCreateEditModal";
import GenericNotification from "@/components/notification/genericNotification";
import dayjs from "dayjs";
import AppointmentTreeItemUploadButton from "@/components/appointmentDashboard/appointmentTreeItemUploadButton";
import SubjectOverviewAppointmentCreateModal from "@/components/subjectOverview/subjectOverviewAppointmentCreateModal";
import classNames from "classnames";
import { useDispatch } from "react-redux";
import { setTableSearch } from "@/components/state/tableSearch/tableSearchSlice";
import { KaerIcon } from "@/icons/kaerIcon";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { api } from "@/helper/api";
import GenericCrudTableModal from "@/components/tables/genericCrudTableModal";
import { getAdminAppointmentDashboardUrl } from "@/components/appointmentDashboard/getAdminAppointmentDashboardUrl";
import AppointmentContextAccordion from "@/components/appointmentDashboard/appointmentContextAccordion";

interface Props {
  appointment: any;
  appointmentId: string;
  handleSetError: (error: any) => void;
  handleSetCurrentAppointment: (appointment: any) => void;
  executedByOptions: any;
  executedByPresetValue: any;
  handleExecutedByChange: (selectedOption: any) => void;
  assistedByOptions?: any;
  assistedByPresetValue?: any;
  handleAssistedByChange?: (selectedOption: any) => void;
  formId?: string;
  findingId?: string;
  handleTriggerReload?: () => void;
  actionsExpanded?: boolean;
  onActionsExpandedChange?: (expanded: boolean) => void;
  renderAppointmentFormLayout?: (sections: { toolbar: ReactNode; actions: ReactNode }) => ReactNode;
}

const AppointmentTreeItemHeaderbar: FC<Props> = ({
  appointment,
  appointmentId,
  handleSetError,
  handleSetCurrentAppointment,
  executedByOptions,
  executedByPresetValue,
  handleExecutedByChange,
  assistedByOptions,
  assistedByPresetValue,
  handleAssistedByChange,
  formId,
  findingId,
  handleTriggerReload,
  actionsExpanded = true,
  onActionsExpandedChange,
  renderAppointmentFormLayout,
}) => {
  const { t } = useTranslation();
  const isAppointmentFormView = formId !== undefined || findingId !== undefined;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { showConfirmationModalAsync } = useConfirmationModal();

  const [todoModalOpen, setTodoModalOpen] = useState<boolean>(false);
  // const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [createAppointmentModalOpen, setCreateAppointmentModalOpen] =
    useState<boolean>(false);
  const [addExaminationModalOpen, setAddExaminationModalOpen] =
    useState<boolean>(false);
  const [modalPresetValues, setModalPresetValues] = useState<any>([]);
  const [successForNotification, setSuccessForNotification] =
    useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();
  const [prefilledResponsibleSubject, setPrefilledResponsibleSubject] = useState<
    { value: string; label: string } | undefined
  >(undefined);

  const existingExaminationIds = Array.isArray(appointment?.examination_set)
    ? appointment.examination_set.map((exam: any) => exam.id)
    : [];
  const appointmentCategoryOption =
    appointment?.category?.id
      ? [{
        value: appointment.category.id,
        label: appointment.category.name,
      }]
      : [];

  const finalStateStatus = [
    "post_processing_required",
    "waiting_for_review",
    "review_preparation_required",
    "completed",
    "canceled",
    "canceled_not_in_time",
    "missed",
    "declined",
  ];
  const addExaminationAllowedStatus = [
    "open",
    "prefilled_by_user",
    "in_progress",
  ];

  const addExaminationModalColumns = [
    {
      header: t("category"),
      accessorKey: "category_name",
      id: "category",
      enableForCreate: true,
      selectOptions: appointmentCategoryOption,
      required: true,
      errorMessage: t("field_required"),
      disableColumn: true,
    },
    {
      header: t("examination"),
      accessorKey: "examination_name",
      id: "examination",
      enableForCreate: true,
      autoFocus: true,
      filterByCategory: true,
      entity: "examination",
      asyncEntitySearch: true,
      multiselect: true,
      additionalUrlParameters: {
        include_relations: true,
        "id__in!": existingExaminationIds,
      },
      required: true,
      errorMessage: t("field_required"),
    },
  ];

  const resetStateOnCloseNotification = () => {
    setSuccessForNotification(false);
  };

  const handleError = (error: any) => {
    handleSetError(error);
  };

  const reducedStatus = () => {
    const { appointmentStatus } = getAppointmentDetails(appointment.status);
    return (
      <Tooltip title={appointment.status} placement="top">
        <span>{appointmentStatus}</span>
      </Tooltip>
    );
  };

  const getReadableDate = () => {
    const parsedDate = new Date(appointment.datetime);

    return parsedDate.toLocaleString("de", {
      timeZone: "Europe/Berlin",
      timeStyle: "short",
      dateStyle: "medium",
    });
  };

  const handleNavigateBack = () => {
    if (formId === undefined) {
      navigate("/appointmentDashboard");
    } else {
      navigate("/appointmentDashboard/" + appointmentId);
    }
  };

  const disableAllActions = () => {
    if (findingId !== undefined && formId !== undefined) {
      return true;
    }
    return false;
  };

  const handleCreateTodo = () => {
    setNotificationMessage(t("todo_created_successfully"));
    setNotificationVariant("success");
    setSuccessForNotification(true);
    setTodoModalOpen(true);
  };

  const handleTodoModalClose = () => {
    setTodoModalOpen(false);
  };

  const handleTodoSuccess = () => {
    setNotificationMessage(t("todo_created_successfully"));
    setNotificationVariant("success");
    setSuccessForNotification(true);
  };

  const handleOpenCreateAppointmentModal = () => {
    setCreateAppointmentModalOpen(true);
  };

  const handleCloseCreateAppointmentModal = () => {
    setCreateAppointmentModalOpen(false);
  };

  const handleAppointmentCreated = () => {
    setCreateAppointmentModalOpen(false);
    try {
      window.dispatchEvent(
        new CustomEvent(
          "new_appointment_created_in_appointment_dashboard_detail_view",
          {
            detail: { subjectId: appointment?.subject?.id },
          },
        ),
      );
    } catch {
      // no-op
    }
  };

  useEffect(() => {
    const id = appointment?.executed_by?.id;
    if (!id) {
      setPrefilledResponsibleSubject(undefined);
      return;
    }

    setPrefilledResponsibleSubject({
      value: String(id),
      label: String(appointment?.executed_by?.name ?? ""),
    });
  }, [
    appointment?.executed_by?.id,
    appointment?.executed_by?.name,
  ]);

  const getAppointmentTodoTitle = () => {
    const subjectName = String(appointment?.subject?.name || "").trim();
    const companyName = String(appointment?.subject?.company?.name || "").trim();
    const isCompanySubject = Boolean(appointment?.subject?.is_company);
    const dateLabel = appointment?.datetime
      ? dayjs(appointment.datetime).format("DD.MM.YYYY")
      : "";

    const titlePrefix = isCompanySubject
      ? subjectName || companyName
      : subjectName && companyName
        ? `${subjectName} (${companyName})`
        : subjectName || companyName;

    if (titlePrefix && dateLabel) {
      return `${titlePrefix} - ${dateLabel}`;
    }
    return t("open_todo_for_you");
  };

  const dispatchForTasktracking = async (e: React.MouseEvent) => {
    e.preventDefault();
    await dispatch(
      setTableSearch({ searchValue: appointmentId, entity: "taskTracking" }),
    );

    navigate("/settings/taskTracking");
  };
  const changeAppoointmentStatus = async () => {
    const isConfirmed = await showConfirmationModalAsync(
      t("appointment_status_to_confirm", {
        status: t("in_progress"),
      }),
    );
    if (!isConfirmed) return;

    const additionalRouteParts: any = {};
    additionalRouteParts["set_status"] = "/";

    const requestData = {
      status: "in_progress",
    };

    try {
      await new Promise<void>((resolve, reject) => {
        api.genericApiRequest({
          entity: "appointment",
          method: "put",
          successHandler: () => {
            resolve();
          },
          failHandler: (error: any) => {
            reject(error);
          },
          entityId: appointment.id,
          additionalRouteParts: additionalRouteParts,
          submitData: requestData,
        });
      });
    } catch (error: any) {
      handleSetError(error);
    } finally {
      handleTriggerReload?.();
    }
  };

  const openAddExaminationModal = () => {
    // const existingExaminationIds = Array.isArray(appointment?.examination_set)
    //   ? appointment.examination_set.map((exam: any) => exam.id)
    //   : [];
    setModalPresetValues({
      category: appointment?.category?.id,
      // examination: existingExaminationIds,
    });

    setAddExaminationModalOpen(true);
  };

  const addExamination = async (values: any) => {
    const existingExaminationIds = Array.isArray(appointment?.examination_set)
      ? appointment.examination_set.map((exam: any) => exam.id)
      : [];

    const newExaminationIds = Array.isArray(values.examination)
      ? values.examination
      : [];

    const combinedIds = Array.from(
      new Set([...existingExaminationIds, ...newExaminationIds]),
    );

    const result = {
      examination_set: combinedIds,
    };
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    try {
      await new Promise<void>((resolve, reject) => {
        api.genericApiRequest({
          entity: "appointment",
          method: "put",
          entityId: appointmentId,
          successHandler: () => {
            resolve();
          },
          failHandler: (error: any) => {
            reject(error);
          },
          submitData: result,
          config: config,
        });
      });
      setNotificationMessage(t("save_successful"));
      setNotificationVariant("success");
      setSuccessForNotification(true);
      setAddExaminationModalOpen(false);
    } catch (error: any) {
      handleSetError(error);
    } finally {
      handleTriggerReload?.();
    }
  };

  const handleClearDropdown = (fieldName: string) => {
    dispatch(setTableSearch({ searchValue: "", entity: fieldName }));
  };

  const statusAndTime = (
    <div className={styles.statusTextWrapper}>
      <ListItemText
        className={styles.statusText}
        primary={t("status") + ":"}
        secondary={
          <NavLink
            to={`/settings/taskTracking}`}
            onClick={dispatchForTasktracking}
            className={classNames(styles.customLink)}
          >
            {reducedStatus()}
          </NavLink>
        }
        primaryTypographyProps={{ className: styles.primaryText }}
        secondaryTypographyProps={{ className: styles.secondaryText }}
      />
      <ListItemText
        className={styles.statusText}
        primary={t("time_point") + ":"}
        secondary={getReadableDate()}
        primaryTypographyProps={{ className: styles.primaryText }}
        secondaryTypographyProps={{ className: styles.secondaryText }}
      />
    </div>
  );

  const staffAssignments = (
    <Grid container item xs={12} md={6} lg={5} spacing={isAppointmentFormView ? 0 : 1}
      className={isAppointmentFormView ? styles.appointmentFormAssignments : undefined}>
      <Grid item xs={12} sm={6} className={styles.assignmentField}>
        <DropDown
          placeholder={t("executed_by") + " ..."}
          label={t("executed_by")}
          options={executedByOptions}
          defaultValue={executedByPresetValue}
          name="executed_by"
          onChange={handleExecutedByChange}
        />
      </Grid>
      <Grid item xs={12} sm={6} className={styles.assignmentField}>
        <DropDown
          placeholder={t("assisted_by") + " ..."}
          label={t("assisted_by")}
          options={assistedByOptions}
          defaultValue={assistedByPresetValue}
          name="assisted_by"
          onChange={handleAssistedByChange}
          isClearable={true}
          resetInput={() => handleClearDropdown("assistedBy")}
          showConfirmationModalForResetInput={true}
          confirmationModalMessageForResetInput={t(
            "confirm_remove_assisted_by",
          )}
        />
      </Grid>
    </Grid>
  );

  const workflowButtons = (
    <AppointmentTreeItemActionButtons
      appointment={appointment}
      appointmentId={appointmentId}
      handleSetError={handleError}
      handleSetCurrentAppointment={handleSetCurrentAppointment}
      allButtonsDisabled={disableAllActions()}
      executedById={executedByPresetValue}
      className={isAppointmentFormView ? styles.appointmentFormActionButtons : undefined}
    />
  );

  const appointmentControls = (
    <div className={isAppointmentFormView ? styles.appointmentFormControls : undefined}>
      <Grid container className={styles.container} columnSpacing={isAppointmentFormView ? 0 : "1rem"}>
        {!isAppointmentFormView && (
          <Grid container item xs={12} className={styles.firstRow}>
            <Grid item xs={2} md={1} className={styles.navigateBackButton}>
              <Tooltip
                placement="right"
                title={t("back")}
                sx={{ marginBottom: "1rem" }}
              >
                <GenericButton
                  onClick={handleNavigateBack}
                  variant="icon"
                  color="iconBase"
                >
                  <ArrowBackIcon />
                </GenericButton>
              </Tooltip>
            </Grid>
            <Grid
              container
              item
              xs={10}
              md={5}
              lg={6}
              spacing={2}
              className={styles.actionButtonsContainer}
            >
              <Grid item>
                {workflowButtons}
              </Grid>
            </Grid>
            {staffAssignments}
          </Grid>
        )}
        <Grid
          container
          item
          xs={12}
          columnSpacing={isAppointmentFormView ? 0 : "1rem"}
          className={styles.secondRow}
        >
          <Grid item xs={12} className={styles.secondaryButtonsContainer}>
            <div className={styles.headerBarButtonWrapper}>
              <AppointmentTreeItemUploadButton
                useCase="documentWidget"
                renderDocumentCaseAsButton={true}
                renderDocumentCaseAsButtonWithTooltip={true}
                renderDocumentCaseAsButtonTitle={t("upload_document")}
                buttonVariant="outlined"
                subject={appointment?.subject}
                appointmentId={appointmentId}
                handleTriggerReload={handleTriggerReload}
              />
              <GenericButton
                variant="outlined"
                color="primary"
                onClick={handleOpenCreateAppointmentModal}
                startIcon={<MoreTimeIcon />}
              >
                {t("create_appointment")}
              </GenericButton>
              <GenericButton
                variant="outlined"
                color="primary"
                onClick={handleCreateTodo}
                startIcon={<AddTaskIcon />}
              >
                {t("create_todo")}
              </GenericButton>
            </div>
            {!isAppointmentFormView && statusAndTime}
            <div className={styles.extraActionsWrapper}>
              {disableAllActions() ? (
                <Edit style={{ color: "#9e9e9e" }} />
              ) : (
                <>
                  {addExaminationAllowedStatus.includes(appointment.status) && (
                    <Tooltip
                      title={t("add_custom", {
                        subject: t("examination"),
                      })}
                      placement="top"
                    >
                      <GenericButton
                        variant="icon"
                        onClick={openAddExaminationModal}
                      >
                        <KaerIcon
                          icon="addEntry"
                          color="#8c1ec8"
                          width="24"
                          height="24"
                        />
                      </GenericButton>
                    </Tooltip>
                  )}
                  {finalStateStatus.includes(appointment.status) && (
                    <Tooltip
                      title={t("appointment_status_to", {
                        status: t("in_progress"),
                      })}
                      placement="top"
                    >
                      <GenericButton
                        variant="icon"
                        onClick={changeAppoointmentStatus}
                      >
                        <KaerIcon
                          icon="undo"
                          color="#8c1ec8"
                          width="24"
                          height="24"
                        />
                      </GenericButton>
                    </Tooltip>
                  )}
                  <Tooltip
                    title={t("open_custom", {
                      subject: t("appointment_single"),
                    })}
                    placement="top"
                  >
                    <Link
                      to={`/settings/appointmentEditor/${appointmentId}`}
                      target="_blank"
                      className={styles.link}
                    >
                      <Edit />
                    </Link>
                  </Tooltip>
                </>
              )}
            </div>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );

  const toolbar = (
    <div className={styles.appointmentFormToolbar}>
      <div className={styles.appointmentFormStatusGroup}>
        <Tooltip title={t("back")}>
          <GenericButton onClick={handleNavigateBack} variant="icon" color="iconBase" aria-label={t("back")}>
            <ArrowBackIcon />
          </GenericButton>
        </Tooltip>
        <div className={styles.appointmentFormStatus}>
          {statusAndTime}
          {workflowButtons}
        </div>
      </div>
      {staffAssignments}
    </div>
  );
  const actions = isAppointmentFormView ? (
    <AppointmentContextAccordion title={t("appointment_actions")} expanded={actionsExpanded} onChange={(expanded) => onActionsExpandedChange?.(expanded)}>
      {appointmentControls}
    </AppointmentContextAccordion>
  ) : appointmentControls;

  return (
    <>
      {isAppointmentFormView && renderAppointmentFormLayout ? renderAppointmentFormLayout({ toolbar, actions }) : (
        <>
          {isAppointmentFormView && toolbar}
          {actions}
        </>
      )}

      {todoModalOpen && (
        <TodoCreateEditModal
          open={todoModalOpen}
          onClose={handleTodoModalClose}
          onSuccess={handleTodoSuccess}
          preExistingSubject={
            appointment?.subject
              ? {
                value: appointment.subject.id,
                label: appointment.subject.name,
              }
              : undefined
          }
          prefilledName={getAppointmentTodoTitle()}
          hideNonMandatoryFields={true}
          initialCreateMode="kaer_internal"
          enableKaerInternalTab={true}
          prefilledResponsibleSubject={prefilledResponsibleSubject}
          forcedTextTarget={{
            link: getAdminAppointmentDashboardUrl(appointmentId),
            linkTitle: "Termin Dashboard öffnen",
          }}
          showTextTargetWhenHidden={true}
        />
      )}
      {createAppointmentModalOpen && (
        <SubjectOverviewAppointmentCreateModal
          open={createAppointmentModalOpen}
          onClose={handleCloseCreateAppointmentModal}
          detailedSubject={appointment?.subject}
          onAppointmentCreated={handleAppointmentCreated}
        />
      )}
      {addExaminationModalOpen && (
        <GenericCrudTableModal
          open={addExaminationModalOpen}
          columns={addExaminationModalColumns}
          presetValues={modalPresetValues}
          title={t("add")}
          onClose={() => setAddExaminationModalOpen(false)}
          submitButtonText={t("add")}
          onSubmit={addExamination}
          handleError={handleSetError}
        />
      )}
      <GenericNotification
        message={successForNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
    </>
  );
};

export default AppointmentTreeItemHeaderbar;
